import React, { useEffect, useState } from 'react';
import { ClockIcon, CheckCircleIcon, EditIcon, TrashIcon, BellIcon, CalendarIcon } from 'lucide-react';
interface Reminder {
  id: string;
  medicineName: string;
  medicineStrength?: string;
  medicineForm?: string;
  dosage: string;
  frequency: string;
  timesPerDay: number;
  doseTimes: string[];
  daysOfWeek: number[];
  mealTiming: 'before' | 'after' | 'with' | 'none';
  pillsPerDose: number;
  dosesPerIntake: number;
  currentStock?: number;
  autoReorder: boolean;
  nextDose: Date;
  duration: number;
  reminderType: string[];
  notes?: string;
  status: 'active' | 'completed';
  snoozedUntil?: Date;
}
interface MedicineReminderCardProps {
  reminder: Reminder;
  onMarkTaken: (id: string) => void;
  onEdit: (reminder: Reminder) => void;
  onDelete: (id: string) => void;
  onSnooze: (id: string) => void;
}
const DAY_NAMES = ['S', 'M', 'T', 'W', 'T', 'F', 'S'];
type CardStatus = 'missed' | 'due-now' | 'soon' | 'upcoming' | 'snoozed' | 'taken' | 'neutral';
interface CardStatusConfig {
  bgColor: string;
  textColor: string;
  iconColor: string;
  badge: string;
  badgeBg: string;
  badgeText: string;
}
const STATUS_CONFIGS: Record<CardStatus, CardStatusConfig> = {
  missed: {
    bgColor: 'bg-red-50',
    textColor: 'text-red-700',
    iconColor: 'text-red-600',
    badge: 'Missed',
    badgeBg: 'bg-red-100',
    badgeText: 'text-red-800'
  },
  'due-now': {
    bgColor: 'bg-amber-50',
    textColor: 'text-amber-800',
    iconColor: 'text-amber-600',
    badge: 'Due Now',
    badgeBg: 'bg-amber-100',
    badgeText: 'text-amber-900'
  },
  soon: {
    bgColor: 'bg-yellow-50',
    textColor: 'text-yellow-800',
    iconColor: 'text-yellow-600',
    badge: 'Soon',
    badgeBg: 'bg-yellow-100',
    badgeText: 'text-yellow-900'
  },
  upcoming: {
    bgColor: 'bg-sky-50',
    textColor: 'text-sky-800',
    iconColor: 'text-sky-600',
    badge: 'Upcoming',
    badgeBg: 'bg-sky-100',
    badgeText: 'text-sky-900'
  },
  snoozed: {
    bgColor: 'bg-violet-50',
    textColor: 'text-violet-800',
    iconColor: 'text-violet-600',
    badge: 'Snoozed',
    badgeBg: 'bg-violet-100',
    badgeText: 'text-violet-900'
  },
  taken: {
    bgColor: 'bg-green-50',
    textColor: 'text-green-800',
    iconColor: 'text-green-600',
    badge: 'Taken ✓',
    badgeBg: 'bg-green-100',
    badgeText: 'text-green-900'
  },
  neutral: {
    bgColor: 'bg-gray-50',
    textColor: 'text-gray-800',
    iconColor: 'text-gray-600',
    badge: 'Scheduled',
    badgeBg: 'bg-gray-100',
    badgeText: 'text-gray-900'
  }
};
export function MedicineReminderCard({
  reminder,
  onMarkTaken,
  onEdit,
  onDelete,
  onSnooze
}: MedicineReminderCardProps) {
  const [showPulse, setShowPulse] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);
  const getCardStatus = (): CardStatus => {
    const now = new Date();
    const nextDoseTime = reminder.nextDose.getTime();
    const currentTime = now.getTime();
    const diffMs = nextDoseTime - currentTime;
    const diffMinutes = Math.floor(diffMs / (1000 * 60));
    // Priority order: Snoozed > Taken > Missed > Due Now > Soon > Upcoming > Neutral
    // Check if snoozed
    if (reminder.snoozedUntil && reminder.snoozedUntil.getTime() > currentTime) {
      return 'snoozed';
    }
    // Check if taken
    if (reminder.status === 'completed') {
      return 'taken';
    }
    // Check if missed (past due by more than 1 minute)
    if (diffMinutes < -1) {
      return 'missed';
    }
    // Check if due now (within 5 minutes before or after)
    if (diffMinutes >= -5 && diffMinutes <= 5) {
      // Trigger pulse animation for mobile only
      if (isMobile && !showPulse) {
        setShowPulse(true);
        setTimeout(() => setShowPulse(false), 5000); // 5 seconds
      }
      return 'due-now';
    }
    // Check if soon (5-30 minutes before)
    if (diffMinutes > 5 && diffMinutes <= 30) {
      return 'soon';
    }
    // Check if upcoming (more than 30 minutes, same day)
    const isToday = now.toDateString() === reminder.nextDose.toDateString();
    if (diffMinutes > 30 && isToday) {
      return 'upcoming';
    }
    return 'neutral';
  };
  const getNextDoseText = (nextDose: Date) => {
    const now = new Date();
    const diff = nextDose.getTime() - now.getTime();
    const hours = Math.floor(diff / (1000 * 60 * 60));
    const minutes = Math.floor(diff % (1000 * 60 * 60) / (1000 * 60));
    if (diff < -60000) {
      // More than 1 minute overdue
      const overdueMinutes = Math.abs(minutes);
      const overdueHours = Math.abs(hours);
      if (overdueHours > 0) {
        return `${overdueHours}h ${overdueMinutes}m ago`;
      }
      return `${overdueMinutes}m ago`;
    }
    if (hours === 0 && minutes <= 5) return 'Now';
    if (hours === 0) return `In ${minutes}m`;
    if (hours < 24) return `In ${hours}h ${minutes}m`;
    return `In ${Math.floor(hours / 24)} days`;
  };
  const getMealIcon = (timing: string) => {
    switch (timing) {
      case 'before':
        return '🍽️';
      case 'after':
        return '🍴';
      case 'with':
        return '🥗';
      default:
        return null;
    }
  };
  const cardStatus = getCardStatus();
  const statusConfig = STATUS_CONFIGS[cardStatus];
  const timeText = getNextDoseText(reminder.nextDose);
  // Determine border color based on status
  const getBorderColor = () => {
    switch (cardStatus) {
      case 'missed':
        return 'border-red-200';
      case 'due-now':
        return 'border-amber-200';
      case 'soon':
        return 'border-yellow-200';
      case 'upcoming':
        return 'border-sky-200';
      case 'snoozed':
        return 'border-violet-200';
      case 'taken':
        return 'border-green-200';
      default:
        return 'border-gray-200';
    }
  };
  return <div className={`${statusConfig.bgColor} ${getBorderColor()} border-2 rounded-xl p-4 shadow-md transition-all hover:shadow-lg`} role="article" aria-live="polite" aria-label={`${reminder.medicineName} reminder - ${statusConfig.badge}`} data-id="element-6261" data-mp="31gz33lah0s26">
      {/* Header */}
      <div className="flex items-start justify-between mb-3" data-id="element-6262" data-mp="35h9bt5pndwkq">
        <div className="flex-1" data-id="element-6263" data-mp="3kervvurdrllu">
          <div className="flex items-center gap-2 mb-1" data-id="element-6264" data-mp="3ke09najob088">
            <h3 className={`font-bold ${statusConfig.textColor}`} data-id="element-6265" data-mp="3fggkhflft31i">
              {reminder.medicineName}
            </h3>
            <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${statusConfig.badgeBg} ${statusConfig.badgeText}`} data-id="element-6266" data-mp="3t7v8y1y45wfy">
              {statusConfig.badge}
              {cardStatus === 'missed' && ` • ${timeText}`}
              {cardStatus === 'snoozed' && ' 10m'}
            </span>
          </div>
          {reminder.medicineStrength && <p className={`text-xs ${statusConfig.textColor} opacity-75`} data-id="element-6267" data-mp="lndjb61nzkp1">
              {reminder.medicineStrength} • {reminder.medicineForm}
            </p>}
        </div>
        <div className="flex gap-1" data-id="element-6268" data-mp="boy15skm7lro">
          <button onClick={() => onEdit(reminder)} className={`p-1.5 ${statusConfig.textColor} opacity-60 hover:opacity-100 hover:bg-white hover:bg-opacity-50 rounded-lg transition-all`} aria-label="Edit reminder" data-id="element-6269" data-mp="bppo6f05cb6c">
            <EditIcon className="w-4 h-4" data-id="element-6270" data-mp="7ncfbhq3rx5c" />
          </button>
          <button onClick={() => onDelete(reminder.id)} className="p-1.5 text-red-600 opacity-60 hover:opacity-100 hover:bg-white hover:bg-opacity-50 rounded-lg transition-all" aria-label="Delete reminder" data-id="element-6271" data-mp="7ovwrbnhwe8p">
            <TrashIcon className="w-4 h-4" data-id="element-6272" data-mp="3g2mf2j6isq9y" />
          </button>
        </div>
      </div>
      {/* Dosage Info */}
      <div className="mb-3 p-2 bg-white bg-opacity-50 rounded-lg" data-id="element-6273" data-mp="3k4zk15dg0k5o">
        <div className="flex items-center justify-between text-xs" data-id="element-6274" data-mp="4eumtj9vy9p2">
          <span className={statusConfig.textColor} data-id="element-6275" data-mp="4e33b6tkc2f5">
            {reminder.pillsPerDose} pill(s) × {reminder.dosesPerIntake} dose(s)
          </span>
          <span className={`font-medium ${statusConfig.textColor}`} data-id="element-6276" data-mp="3qljghmlr0ofi">
            {reminder.timesPerDay}x per day
          </span>
        </div>
        {reminder.mealTiming !== 'none' && <div className={`flex items-center gap-1 mt-1 text-xs ${statusConfig.textColor}`} data-id="element-6277" data-mp="3hl4l1uf2lsya">
            <span data-id="element-6278" data-mp="3cueck8bcl9zd">{getMealIcon(reminder.mealTiming)}</span>
            <span className="capitalize" data-id="element-6279" data-mp="3csthmhl4fp0z">{reminder.mealTiming} meal</span>
          </div>}
      </div>
      {/* Days of Week */}
      <div className="flex gap-1 mb-3" data-id="element-6280" data-mp="32x9ou41ut6gg">
        {DAY_NAMES.map((day, index) => <div key={index} className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-medium ${reminder.daysOfWeek.includes(index) ? `${statusConfig.iconColor} bg-white bg-opacity-70` : 'bg-white bg-opacity-30 text-gray-400'}`} data-id="element-6281" data-mp="2xx5ih6unts3y" data-mpi={index}>
            {day}
          </div>)}
      </div>
      {/* Next Dose */}
      <div className="flex items-center justify-between p-3 rounded-lg mb-3 bg-white bg-opacity-50" data-id="element-6282" data-mp="1dhftri74vfpz">
        <div data-id="element-6283" data-mp="1icczog9498yn">
          <p className={`text-xs ${statusConfig.textColor} opacity-75 mb-1`} data-id="element-6284" data-mp="1icmo3zeij828">
            Next dose
          </p>
          <p className={`text-sm font-semibold ${statusConfig.textColor}`} data-id="element-6285" data-mp="pq22lfkpxq6l">
            {reminder.nextDose.toLocaleTimeString('en-US', {
            hour: '2-digit',
            minute: '2-digit'
          })}
          </p>
        </div>
        <div className="flex items-center gap-2" data-id="element-6286" data-mp="zn6g6r0i0dbs">
          {cardStatus === 'due-now' && showPulse && isMobile && <BellIcon className={`w-5 h-5 ${statusConfig.iconColor} animate-pulse`} data-id="element-6287" data-mp="14fzlph3sjpox" />}
          <div className="text-right" data-id="element-6288" data-mp="bydqhcagz1pm">
            <p className={`text-xs font-medium ${statusConfig.textColor}`} data-id="element-6289" data-mp="bxc3c9g6iorj">
              {cardStatus === 'missed' && '⚠️ '}
              {cardStatus === 'due-now' && '⏰ '}
              {cardStatus === 'soon' && '🔔 '}
              {timeText}
            </p>
          </div>
        </div>
      </div>
      {/* Dose Times */}
      <div className="mb-3" data-id="element-6290" data-mp="331htfbc6msvm">
        <p className={`text-xs ${statusConfig.textColor} opacity-75 mb-2`} data-id="element-6291" data-mp="37zt2vkykf1db">
          Today's doses:
        </p>
        <div className="flex flex-wrap gap-2" data-id="element-6292" data-mp="2acax8j07afdj">
          {reminder.doseTimes.map((time, index) => <span key={index} className={`text-xs ${statusConfig.badgeBg} ${statusConfig.badgeText} px-2 py-1 rounded-full font-medium`} data-id="element-6293" data-mp="2fam6ario8lup" data-mpi={index}>
              {time}
            </span>)}
        </div>
      </div>
      {/* Notification Channels */}
      <div className="flex items-center gap-2 mb-3" data-id="element-6294" data-mp="1rlt5ohr3tptr">
        {reminder.reminderType.map(type => <span key={type} className={`text-xs ${statusConfig.badgeBg} ${statusConfig.badgeText} px-2 py-1 rounded-full`} data-id="element-6295" data-mp="1wke8moxqqnlw">
            {type === 'push' && '🔔'}
            {type === 'sms' && '💬'}
            {type === 'whatsapp' && '📱'}
            {type === 'email' && '📧'}
          </span>)}
      </div>
      {/* Stock Info */}
      {reminder.currentStock !== undefined && <div className="mb-3 p-2 bg-white bg-opacity-50 border border-white border-opacity-50 rounded-lg" data-id="element-6296" data-mp="3vu38cyvdj6rw">
          <div className="flex items-center justify-between text-xs" data-id="element-6297" data-mp="3qwv0br3xwj4d">
            <span className={statusConfig.textColor} data-id="element-6298" data-mp="3m4tdjb95ba03">Stock remaining:</span>
            <span className={`font-medium ${statusConfig.textColor}`} data-id="element-6299" data-mp="3i3rdwtbbgvxl">
              {reminder.currentStock} pills
            </span>
          </div>
          {reminder.autoReorder && <p className={`text-xs ${statusConfig.iconColor} mt-1 font-medium`} data-id="element-6300" data-mp="3d2tw4hu7y3wm">
              🔄 Auto-reorder enabled
            </p>}
        </div>}
      {/* Notes */}
      {reminder.notes && <p className={`text-xs ${statusConfig.textColor} opacity-75 mb-3 italic`} data-id="element-6301" data-mp="2tiv24lxq0ovj">
          {reminder.notes}
        </p>}
      {/* Action Buttons */}
      <div className="flex gap-2" data-id="element-6302" data-mp="2yfv8kxyhftn6">
        <button onClick={() => onMarkTaken(reminder.id)} className={`flex-1 flex items-center justify-center gap-2 ${statusConfig.iconColor} bg-white px-3 py-2 rounded-lg text-sm font-medium hover:bg-opacity-90 transition-colors shadow-sm`} aria-label="Mark as taken" data-id="element-6303" data-mp="2kioql5ll1yz0">
          <CheckCircleIcon className="w-4 h-4" data-id="element-6304" data-mp="3h8pbuumqtxbk" />
          Taken
        </button>
        {(cardStatus === 'due-now' || cardStatus === 'soon') && <button onClick={() => onSnooze(reminder.id)} className="px-3 py-2 bg-white text-gray-700 rounded-lg text-sm font-medium hover:bg-opacity-90 transition-colors shadow-sm" aria-label="Snooze reminder" data-id="element-6305" data-mp="33eyeslq785wq">
            Snooze
          </button>}
      </div>
    </div>;
}